import React, { forwardRef, useMemo } from 'react';
import { XStack } from 'tamagui';
import { DateInput } from './DateInput';
import { TimeInput } from './TimeInput';
import type { TextInput } from 'react-native';

/**
 * Composite datetime input — date picker + time mask side by side,
 * round-tripping to a single ISO 'YYYY-MM-DDTHH:mm' string.
 *
 * Why composed rather than a single `<input type="datetime-local">`:
 *
 *  - On native, no built-in datetime-local exists; we already wrap
 *    `@react-native-community/datetimepicker` for date and have a
 *    separate digit-mask `TimeInput`. Splitting the surface keeps both
 *    edit modes accessible.
 *  - On web, the native datetime-local widget is patchy (Safari
 *    mobile in particular). The split DateInput + TimeInput combo
 *    works the same on every platform.
 *
 * Empty value → both halves empty. Either half empty → emits an empty
 * string so the caller can short-circuit to the previously-persisted
 * value (mirroring the existing persist patterns in /journey, /stay).
 */
export interface DateTimeInputProps {
  /** Current datetime as 'YYYY-MM-DDTHH:mm' or empty. */
  value: string;
  /** Called with 'YYYY-MM-DDTHH:mm' or '' when the user clears either half. */
  onChangeText: (value: string) => void;
  /** Disables the calendar icon on the date half (still keyboard-editable). */
  showCalendar?: boolean | undefined;
  /** Optional minimum selectable date (forwarded to the date picker). */
  minimumDate?: Date | undefined;
  /**
   * IANA tz the time half is anchored to — forwarded to `TimeInput`
   * for screen-reader hint enrichment ("Enter time in Asia/Tokyo").
   * Per ADR-0076. Pass `JourneyAnchor.timezone` at the call site.
   */
  timeZone?: string | undefined;
}

function splitDateTime(iso: string): { date: string; time: string } {
  if (!iso) return { date: '', time: '' };
  const idx = iso.indexOf('T');
  if (idx < 0) return { date: iso, time: '' };
  return {
    date: iso.slice(0, idx),
    time: iso.slice(idx + 1, idx + 6), // 'HH:mm'
  };
}

function combine(date: string, time: string): string {
  if (!date || !time) return '';
  return `${date}T${time}`;
}

export const DateTimeInput = forwardRef<TextInput | null, DateTimeInputProps>(
  ({ value, onChangeText, showCalendar = true, minimumDate, timeZone }, _ref) => {
    const { date, time } = useMemo(() => splitDateTime(value), [value]);

    const handleDateChange = (next: string) => {
      onChangeText(combine(next, time));
    };
    const handleTimeChange = (next: string) => {
      onChangeText(combine(date, next));
    };

    return (
      <XStack gap="$2" alignItems="center">
        <XStack flex={1} minWidth={0}>
          <DateInput
            value={date}
            onChangeText={handleDateChange}
            showCalendar={showCalendar}
            minimumDate={minimumDate}
            variant="inline"
          />
        </XStack>
        <XStack flex={1} minWidth={0}>
          <TimeInput
            value={time}
            onChangeText={handleTimeChange}
            showFormatHint={false}
            variant="inline"
            {...(timeZone ? { timeZone } : null)}
          />
        </XStack>
      </XStack>
    );
  },
);

DateTimeInput.displayName = 'DateTimeInput';
